import config from "../config.js";

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

let csrfToken = "";
let csrfPromise = null;

export class ApiClientError extends Error {
  constructor(message, status = 0) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
  }
}

export const resetCSRFToken = () => {
  csrfToken = "";
  csrfPromise = null;
};

const fetchCSRFToken = async () => {
  const response = await fetch(`${config.serverUri}/private/csrf`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new ApiClientError("CSRF 토큰을 받지 못했습니다.", response.status);
  }
  const data = await response.json();
  if (!data?.token) {
    throw new ApiClientError("CSRF 토큰 응답이 잘못되었습니다.", response.status);
  }
  csrfToken = data.token;
  return csrfToken;
};

export const getCSRFToken = async ({ forceRefresh = false } = {}) => {
  if (forceRefresh) resetCSRFToken();
  if (csrfToken) return csrfToken;
  if (!csrfPromise) {
    csrfPromise = fetchCSRFToken().finally(() => {
      csrfPromise = null;
    });
  }
  return csrfPromise;
};

const isCSRFError = async (response) => {
  if (response.status !== 403) return false;
  try {
    const data = await response.clone().json();
    return data?.code === "csrf_invalid";
  } catch (_) {
    return false;
  }
};

const requestWithToken = (input, init, token) => {
  const headers = new Headers(init.headers || {});
  headers.set("X-CSRF-Token", token);
  return fetch(input, {
    ...init,
    credentials: init.credentials || "include",
    headers,
  });
};

export const apiFetch = async (input, init = {}) => {
  const method = String(init.method || "GET").toUpperCase();
  if (SAFE_METHODS.has(method)) {
    return fetch(input, init);
  }

  let token = await getCSRFToken();
  let response = await requestWithToken(input, init, token);
  if (!(await isCSRFError(response))) return response;

  token = await getCSRFToken({ forceRefresh: true });
  response = await requestWithToken(input, init, token);
  return response;
};
